import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Package, Tag, Edit2, Check, X, Truck, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ServiceSettings = ({ formData, handleChange }) => {
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [newServiceCostPrice, setNewServiceCostPrice] = useState('');
  const [newServiceCurrency, setNewServiceCurrency] = useState('USD');
  const [newServiceCategory, setNewServiceCategory] = useState('');
  const [newServiceSupplier, setNewServiceSupplier] = useState('');
  
  // Arama state'i
  const [searchTerm, setSearchTerm] = useState('');
  
  // Kategori yönetimi state'leri
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  
  // Tedarikçi yönetimi state'leri
  const [newSupplierName, setNewSupplierName] = useState('');
  const [editingSupplierIndex, setEditingSupplierIndex] = useState(null);
  const [editingSupplierName, setEditingSupplierName] = useState('');
  
  const serviceItems = formData.service_items || [];
  
  // Arama - filtrelenmiş hizmet kalemleri
  const filteredServiceItems = useMemo(() => {
    if (!searchTerm.trim()) return serviceItems;
    const term = searchTerm.toLowerCase();
    return serviceItems.filter(service => 
      service.name?.toLowerCase().includes(term) ||
      service.supplier?.toLowerCase().includes(term)
    );
  }, [serviceItems, searchTerm]);
  
  // Aynı isimde hizmet kalemi var mı kontrol et
  const duplicateWarning = useMemo(() => {
    if (!newServiceName.trim()) return null;
    const inputName = newServiceName.trim().toLowerCase();
    const existing = serviceItems.find(s => s.name?.toLowerCase() === inputName);
    if (existing) {
      return `"${existing.name}" adında bir hizmet kalemi zaten mevcut!`;
    }
    // Benzer isimler için de uyar
    const similar = serviceItems.find(s => {
      const name = s.name?.toLowerCase() || '';
      return name.includes(inputName) || inputName.includes(name);
    });
    if (similar && similar.name?.toLowerCase() !== inputName) {
      return `Benzer bir hizmet kalemi var: "${similar.name}"`;
    }
    return null;
  }, [newServiceName, serviceItems]);
  
  // Tüm kategoriler (artık hepsi düzenlenebilir)
  const allCategories = formData.service_categories || [];
  
  // Tüm tedarikçiler (artık hepsi düzenlenebilir)
  const allSuppliers = formData.service_suppliers || [];

  // Hizmet işlemleri
  const addService = () => {
    if (!newServiceName.trim()) {
      toast.error('Hizmet adı giriniz');
      return;
    }
    
    // Aynı isimde hizmet kalemi kontrolü
    const inputName = newServiceName.trim().toLowerCase();
    const existing = serviceItems.find(s => s.name?.toLowerCase() === inputName);
    if (existing) {
      toast.error(`"${existing.name}" adında bir hizmet kalemi zaten mevcut!`);
      return;
    }
    
    const newService = {
      name: newServiceName.trim(),
      default_price: parseFloat(newServicePrice) || 0,
      cost_price: parseFloat(newServiceCostPrice) || 0,
      currency: newServiceCurrency,
      category: newServiceCategory || 'other',
      supplier: newServiceSupplier || '',
      auto_add_transit: false,
      auto_add_inspection: false,
      auto_add_other: false,
      vat_enabled: false,
      vat_rate: 20
    };
    
    handleChange('service_items', [...serviceItems, newService]);
    setNewServiceName('');
    setNewServicePrice('');
    setNewServiceCostPrice('');
    setNewServiceCurrency('USD');
    setNewServiceCategory('');
    setNewServiceSupplier('');
    toast.success('Hizmet kalemi eklendi');
  };

  const updateService = (index, field, value) => {
    const updated = [...serviceItems];
    updated[index] = { ...updated[index], [field]: value };
    handleChange('service_items', updated);
  };

  const deleteService = (index) => {
    const serviceName = serviceItems[index]?.name || 'Bu hizmet';
    if (window.confirm(`"${serviceName}" hizmetini silmek istediğinize emin misiniz?`)) {
      const updated = serviceItems.filter((_, i) => i !== index);
      handleChange('service_items', updated);
      toast.success('Hizmet kalemi silindi');
    }
  };
  
  // Kategori işlemleri
  const addCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Kategori adı giriniz');
      return;
    }
    
    if (allCategories.some(c => c.name.toLowerCase() === newCategoryName.trim().toLowerCase())) {
      toast.error('Bu kategori zaten mevcut');
      return;
    }
    
    const newCategory = {
      id: `custom_${Date.now()}`,
      name: newCategoryName.trim()
    };
    
    handleChange('service_categories', [...allCategories, newCategory]);
    setNewCategoryName('');
    toast.success('Kategori eklendi');
  };
  
  const startEditCategory = (index) => {
    setEditingCategoryIndex(index);
    setEditingCategoryName(allCategories[index].name);
  };
  
  const saveEditCategory = () => {
    if (!editingCategoryName.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }
    
    const updated = [...allCategories];
    updated[editingCategoryIndex] = { 
      ...updated[editingCategoryIndex], 
      name: editingCategoryName.trim() 
    };
    handleChange('service_categories', updated);
    setEditingCategoryIndex(null);
    setEditingCategoryName('');
    toast.success('Kategori güncellendi');
  };
  
  const cancelEditCategory = () => {
    setEditingCategoryIndex(null);
    setEditingCategoryName('');
  };
  
  const deleteCategory = (index) => {
    const categoryName = allCategories[index]?.name || 'Bu kategori';
    if (window.confirm(`"${categoryName}" kategorisini silmek istediğinize emin misiniz?`)) {
      const updated = allCategories.filter((_, i) => i !== index);
      handleChange('service_categories', updated);
      toast.success('Kategori silindi');
    }
  };
  
  // Tedarikçi işlemleri
  const addSupplier = () => {
    if (!newSupplierName.trim()) {
      toast.error('Tedarikçi adı giriniz');
      return;
    }
    
    if (allSuppliers.some(s => s.name.toLowerCase() === newSupplierName.trim().toLowerCase())) {
      toast.error('Bu tedarikçi zaten mevcut');
      return;
    }
    
    const newSupplier = {
      id: `custom_sup_${Date.now()}`,
      name: newSupplierName.trim()
    };
    
    handleChange('service_suppliers', [...allSuppliers, newSupplier]);
    setNewSupplierName('');
    toast.success('Tedarikçi eklendi');
  };
  
  const startEditSupplier = (index) => {
    setEditingSupplierIndex(index);
    setEditingSupplierName(allSuppliers[index].name);
  };
  
  const saveEditSupplier = () => {
    if (!editingSupplierName.trim()) {
      toast.error('Tedarikçi adı boş olamaz');
      return;
    }
    
    const updated = [...allSuppliers];
    updated[editingSupplierIndex] = { 
      ...updated[editingSupplierIndex], 
      name: editingSupplierName.trim() 
    };
    handleChange('service_suppliers', updated);
    setEditingSupplierIndex(null);
    setEditingSupplierName('');
    toast.success('Tedarikçi güncellendi');
  };
  
  const cancelEditSupplier = () => {
    setEditingSupplierIndex(null);
    setEditingSupplierName('');
  };
  
  const deleteSupplier = (index) => {
    const supplierName = allSuppliers[index]?.name || 'Bu tedarikçi';
    if (window.confirm(`"${supplierName}" tedarikçisini silmek istediğinize emin misiniz?`)) {
      const updated = allSuppliers.filter((_, i) => i !== index);
      handleChange('service_suppliers', updated);
      toast.success('Tedarikçi silindi');
    }
  };

  return (
    <div className="space-y-6">
      {/* Kategori ve Tedarikçi Yönetimi - Yan Yana */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Kategori Yönetimi */}
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Tag size={20} color="#f59e0b" />
              Kategori Yönetimi
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.85rem' }}>
              Hizmet kalemlerinin gruplandırılması için kategoriler.
            </p>
            
            {/* Yeni Kategori Ekleme */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Yeni kategori adı..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
              <button
                type="button"
                onClick={addCategory}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem'
                }}
              >
                <Plus size={16} />
                Ekle
              </button>
            </div>
            
            {/* Mevcut Kategoriler - Hepsi düzenlenebilir */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {allCategories.map((cat, index) => (
                <div key={cat.id} style={{
                  padding: '0.3rem 0.6rem',
                  background: '#fef3c7',
                  borderRadius: '16px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}>
                  {editingCategoryIndex === index ? (
                    <>
                      <input
                        type="text"
                        value={editingCategoryName}
                        onChange={(e) => setEditingCategoryName(e.target.value)}
                        style={{ padding: '0.15rem 0.3rem', borderRadius: '4px', border: '1px solid #f59e0b', fontSize: '0.8rem', width: '100px' }}
                        autoFocus
                      />
                      <button onClick={saveEditCategory} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                        <Check size={12} color="#16a34a" />
                      </button>
                      <button onClick={cancelEditCategory} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                        <X size={12} color="#dc2626" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{cat.name}</span>
                      <button onClick={() => startEditCategory(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Düzenle">
                        <Edit2 size={11} color="#92400e" />
                      </button>
                      <button onClick={() => deleteCategory(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Sil">
                        <Trash2 size={11} color="#dc2626" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Tedarikçi Yönetimi */}
        <div className="card">
          <div className="card-body">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Truck size={20} color="#3b82f6" />
              Tedarikçi Yönetimi
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.85rem' }}>
              Hizmet kalemlerinin alındığı tedarikçiler.
            </p>
            
            {/* Yeni Tedarikçi Ekleme */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Yeni tedarikçi adı..."
                  value={newSupplierName}
                  onChange={(e) => setNewSupplierName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addSupplier()}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
              <button
                type="button"
                onClick={addSupplier}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.85rem'
                }}
              >
                <Plus size={16} />
                Ekle
              </button>
            </div>
            
            {/* Mevcut Tedarikçiler - Hepsi düzenlenebilir */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {allSuppliers.map((sup, index) => (
                <div key={sup.id} style={{
                  padding: '0.3rem 0.6rem',
                  background: '#dbeafe',
                  borderRadius: '16px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: '#1e40af',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}>
                  {editingSupplierIndex === index ? (
                    <>
                      <input
                        type="text"
                        value={editingSupplierName}
                        onChange={(e) => setEditingSupplierName(e.target.value)}
                        style={{ padding: '0.15rem 0.3rem', borderRadius: '4px', border: '1px solid #3b82f6', fontSize: '0.8rem', width: '100px' }}
                        autoFocus
                      />
                      <button onClick={saveEditSupplier} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                        <Check size={12} color="#16a34a" />
                      </button>
                      <button onClick={cancelEditSupplier} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                        <X size={12} color="#dc2626" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span>{sup.name}</span>
                      <button onClick={() => startEditSupplier(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Düzenle">
                        <Edit2 size={11} color="#1e40af" />
                      </button>
                      <button onClick={() => deleteSupplier(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Sil">
                        <Trash2 size={11} color="#dc2626" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hizmet Kalemleri */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Package size={24} color="#8b5cf6" />
              Hizmet Kalemleri
              <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#64748b' }}>({serviceItems.length} adet)</span>
            </h3>
            
            {/* Arama Kutusu */}
            <div style={{ position: 'relative', width: '300px' }}>
              <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Hizmet kalemi ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '36px', margin: 0 }}
                data-testid="service-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Proforma ve faturalarda kullanılacak standart hizmet kalemlerini, fiyatlarını, alış maliyetlerini ve kategorilerini yönetin.
          </p>
          
          {/* Yeni Hizmet Ekleme */}
          <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #86efac' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#16a34a' }}>Yeni Hizmet Ekle</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Hizmet Adı (TR / EN)</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="Örn: Acentelik Ücreti / Agency Fee" 
                  value={newServiceName} 
                  onChange={(e) => setNewServiceName(e.target.value)}
                  style={{ borderColor: duplicateWarning ? '#f59e0b' : undefined }}
                  data-testid="new-service-name-input"
                />
                {/* Duplicate Uyarısı */}
                {duplicateWarning && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    marginTop: '0.5rem', 
                    padding: '0.5rem 0.75rem', 
                    background: '#fef3c7', 
                    borderRadius: '6px', 
                    border: '1px solid #fcd34d',
                    fontSize: '0.8rem',
                    color: '#92400e'
                  }}>
                    <AlertTriangle size={16} color="#f59e0b" />
                    <span>{duplicateWarning}</span>
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Satış Fiyatı</label>
                <input type="number" step="0.01" min="0" className="form-input" placeholder="0.00" value={newServicePrice} onChange={(e) => setNewServicePrice(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Alış Fiyatı (Maliyet)</label>
                <input type="number" step="0.01" min="0" className="form-input" placeholder="0.00" value={newServiceCostPrice} onChange={(e) => setNewServiceCostPrice(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Para Birimi</label>
                <select className="form-input" value={newServiceCurrency} onChange={(e) => setNewServiceCurrency(e.target.value)}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="TRY">TRY</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Kategori</label>
                <select className="form-input" value={newServiceCategory} onChange={(e) => setNewServiceCategory(e.target.value)}>
                  <option value="">Seçiniz...</option>
                  {allCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Tedarikçi</label>
                <select className="form-input" value={newServiceSupplier} onChange={(e) => setNewServiceSupplier(e.target.value)}>
                  <option value="">Seçiniz...</option>
                  {allSuppliers.map(sup => (<option key={sup.id} value={sup.name}>{sup.name}</option>))}
                </select>
              </div>
              <button type="button" onClick={addService} style={{ padding: '0.6rem 1.25rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                <Plus size={18} />
                Ekle
              </button>
            </div>
          </div>

          {/* Mevcut Hizmetler */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.75rem', borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '2fr 90px 90px 130px 100px 50px 50px 50px 45px 45px 40px', gap: '0.5rem', alignItems: 'center' }}>
              <span>Hizmet Adı {searchTerm && <span style={{ fontWeight: 400, color: '#64748b' }}>({filteredServiceItems.length} sonuç)</span>}</span>
              <span style={{ textAlign: 'center' }}>Satış</span>
              <span style={{ textAlign: 'center' }}>Alış</span>
              <span style={{ textAlign: 'center' }}>Kategori</span>
              <span style={{ textAlign: 'center' }}>Tedarikçi</span>
              <span style={{ textAlign: 'center', fontSize: '0.65rem' }}>Transit</span>
              <span style={{ textAlign: 'center', fontSize: '0.65rem' }}>Inspect</span>
              <span style={{ textAlign: 'center', fontSize: '0.65rem' }}>Diğer</span>
              <span style={{ textAlign: 'center', fontSize: '0.65rem' }}>KDV</span>
              <span style={{ textAlign: 'center', fontSize: '0.65rem' }}>KDV%</span>
              <span style={{ textAlign: 'center' }}>Sil</span>
            </div>
            
            {filteredServiceItems.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                {searchTerm ? `"${searchTerm}" için sonuç bulunamadı` : 'Henüz hizmet kalemi eklenmedi'}
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {filteredServiceItems.map((service, filteredIndex) => {
                  // Orijinal index'i bul
                  const originalIndex = serviceItems.findIndex(s => s === service);
                  return (
                    <div key={originalIndex} style={{ padding: '0.5rem 1rem', borderBottom: filteredIndex < filteredServiceItems.length - 1 ? '1px solid #e2e8f0' : 'none', display: 'grid', gridTemplateColumns: '2fr 90px 90px 130px 100px 50px 50px 50px 45px 45px 40px', gap: '0.5rem', alignItems: 'center', background: filteredIndex % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <input type="text" className="form-input" value={service.name || ''} onChange={(e) => updateService(originalIndex, 'name', e.target.value)} style={{ margin: 0, padding: '0.4rem 0.5rem', fontSize: '0.8rem' }} />
                      <input type="number" step="0.01" min="0" className="form-input" value={service.default_price || ''} onChange={(e) => updateService(originalIndex, 'default_price', parseFloat(e.target.value) || 0)} placeholder="0.00" style={{ margin: 0, padding: '0.4rem 0.4rem', fontSize: '0.8rem', textAlign: 'right', background: '#f0fdf4' }} />
                      <input type="number" step="0.01" min="0" className="form-input" value={service.cost_price || ''} onChange={(e) => updateService(originalIndex, 'cost_price', parseFloat(e.target.value) || 0)} placeholder="0.00" style={{ margin: 0, padding: '0.4rem 0.4rem', fontSize: '0.8rem', textAlign: 'right', background: '#fef2f2' }} />
                      <select className="form-input" value={service.category || ''} onChange={(e) => updateService(originalIndex, 'category', e.target.value)} style={{ margin: 0, padding: '0.3rem 0.25rem', fontSize: '0.75rem' }}>
                        <option value="">Seçiniz...</option>
                        {allCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                      </select>
                      <select className="form-input" value={service.supplier || ''} onChange={(e) => updateService(originalIndex, 'supplier', e.target.value)} style={{ margin: 0, padding: '0.3rem 0.2rem', fontSize: '0.7rem' }}>
                        <option value="">Seçiniz...</option>
                        {allSuppliers.map(sup => (<option key={sup.id} value={sup.name}>{sup.name}</option>))}
                      </select>
                      <div style={{ textAlign: 'center' }}><input type="checkbox" checked={service.auto_add_transit === true} onChange={(e) => updateService(originalIndex, 'auto_add_transit', e.target.checked)} style={{ width: '15px', height: '15px', accentColor: '#10b981' }} title="Transit çağrılarına otomatik ekle" /></div>
                      <div style={{ textAlign: 'center' }}><input type="checkbox" checked={service.auto_add_inspection === true} onChange={(e) => updateService(originalIndex, 'auto_add_inspection', e.target.checked)} style={{ width: '15px', height: '15px', accentColor: '#10b981' }} title="Inspection çağrılarına otomatik ekle" /></div>
                      <div style={{ textAlign: 'center' }}><input type="checkbox" checked={service.auto_add_other === true} onChange={(e) => updateService(originalIndex, 'auto_add_other', e.target.checked)} style={{ width: '15px', height: '15px', accentColor: '#10b981' }} title="Diğer çağrılara otomatik ekle" /></div>
                      <div style={{ textAlign: 'center' }}><input type="checkbox" checked={service.vat_enabled === true} onChange={(e) => updateService(originalIndex, 'vat_enabled', e.target.checked)} style={{ width: '15px', height: '15px', accentColor: '#f59e0b' }} title="KDV uygula" /></div>
                      <input type="number" min="0" max="100" className="form-input" value={service.vat_rate || 20} onChange={(e) => updateService(originalIndex, 'vat_rate', parseInt(e.target.value) || 0)} style={{ margin: 0, padding: '0.2rem 0.2rem', fontSize: '0.75rem', textAlign: 'center' }} disabled={!service.vat_enabled} />
                      <div style={{ textAlign: 'center' }}><button type="button" onClick={() => deleteService(originalIndex)} style={{ padding: '0.2rem 0.3rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Sil"><Trash2 size={13} /></button></div>
                    </div>
                  );
                })}
              </div>
            )}}
          </div>
          
          {/* Bilgi Notu */}
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', fontSize: '0.85rem', color: '#1e40af' }}>
            <strong>Not:</strong> 
            <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '0.5rem' }}>
              <li><strong>Satış Fiyatı:</strong> Müşteriye fatura edilen tutar (proformada otomatik gelir)</li>
              <li><strong>Alış Fiyatı:</strong> Tedarikçiye ödenen maliyet (kar hesabında kullanılır)</li>
              <li><strong>Kategori:</strong> İşlem takibinde hizmetlerin gruplandırılması için</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceSettings;
